/**
 * eingang-api.ts — typed feature wrappers + wire→view-model mapping for the
 * T5 Eingang mail-split (Story V10-J).
 *
 * Live wire sources (verified as-built):
 *   GET /tasks     → TaskSummary rows (subject/from/received_at joined from communication)
 *   GET /tasks/:id → SplitViewResponse (communication body + attachments + activity)
 *
 * The Eingang view-model (EingangMail) is mail-centric and richer than the wire
 * shape — per V10-J AC3 the mapping lives HERE, not in the components.
 *
 * Mapping decisions (rendering the SERVER's decisions, no client-side domain logic):
 *   - triage ← task.status (open/awaiting_approval→neu · in_progress→bearbeitung ·
 *     done/cancelled→beantwortet).
 *   - The three list groups render the SERVER's routing decision:
 *     manual→«Zuordnung nötig» (kundeMatched=false) · freigabe→«Niedrige Konfidenz» ·
 *     automated→«Automatisch verarbeitet».
 *   - konfidenz (V10-J2): the REAL task.confidence (numeric(4,3) string → 0–100)
 *     when scored; routing-derived display fallback (automated 95 / freigabe 75 /
 *     manual 45) when null.
 *   - Kunde name/link (V10-J2): kunde_name + subject_id now on the list wire.
 *   - KI fields (Zusammenfassung/Antwort/Felder/Pipeline) are empty in live mode
 *     until the extraction surfaces exist.
 *
 * V10-J2 additions: the Ausgang/Gesendet tabs map GET /communications
 * (bucket=ausgang_entwuerfe|gesendet — Story 7.2 overview) onto AusgangMail:
 *   - status: bucket IS the server's decision → gesendet-bucket rows render
 *     'gesendet'; entwuerfe-bucket rows render 'versandbereit' (dispatched,
 *     awaiting workflow completion — D72-ENTWURF-VIA-OPEN-TASK).
 *   - herkunft: task_routing='automated' → 'ki', else 'manuell'.
 *
 * [Source: packages/shared/src/task.ts] (TaskSummary / SplitViewResponse)
 * [Source: apps/web/src/features/communication/api/tasks-api.ts] (wrapper pattern)
 * [Source: docs/stories/v10-j-web-wiring-core.md AC1/AC3]
 * [Source: docs/stories/v10-j2-web-wiring-rest.md AC6]
 */

import { apiClient } from '../../../lib/api-client';
import type { CommOverviewBucket, CommOverviewItem } from '../../../lib/api-client';
import { dateShortDe, dateTimeShortDe, fileSize } from '../../../lib/format';
import { formatAnonymizedEmailEntry } from '@brokly/shared';
import type {
  CommunicationContentDto,
  TaskRouting,
  TaskStatus,
  TaskSummary,
  SplitViewResponse,
} from '@brokly/shared';
import type { AusgangMail, EingangMail, Triage, Aufgabenstatus, Kanal } from '../types';

/** Map the wire `communication.channel` to the Kommunikation view's Kanal (was hardcoded 'email'). */
function commChannelToKanal(channel: string | null | undefined): Kanal {
  if (channel === 'upload') return 'upload';
  if (channel === 'scanning_center' || channel === 'post') return 'scanning_center';
  if (channel === 'portal') return 'portal';
  return 'email';
}

// ── Display maps (server decision → view value; no domain rules) ─────────────

const TRIAGE_BY_STATUS: Record<TaskStatus, Triage> = {
  open: 'neu',
  awaiting_approval: 'neu',
  in_progress: 'bearbeitung',
  done: 'beantwortet',
  cancelled: 'beantwortet',
};

/** Display-konfidenz per server routing (the list wire has no numeric confidence). */
const KONFIDENZ_BY_ROUTING: Record<TaskRouting, number> = {
  automated: 95,
  freigabe: 75,
  manual: 45,
};

/** Inline Aufgabenstatus per linked task.status; null task → 'keine' (PART A3). */
const AUFGABENSTATUS_BY_STATUS: Record<TaskStatus, Aufgabenstatus> = {
  open: 'offen',
  awaiting_approval: 'wartend',
  in_progress: 'in_bearbeitung',
  done: 'erledigt',
  cancelled: 'erledigt',
};

/** Triage (3-value; drives saved-views) from a comm's task_status. */
const TRIAGE_BY_TASK_STATUS: Record<string, Triage> = {
  open: 'neu',
  awaiting_approval: 'neu',
  in_progress: 'bearbeitung',
  done: 'beantwortet',
  cancelled: 'beantwortet',
};

// ── Mappers ───────────────────────────────────────────────────────────────────

/**
 * task.confidence (numeric(4,3) string) → 0–100 display value; routing-derived
 * fallback when unscored (V10-J2).
 */
export function konfidenzOf(task: TaskSummary): number {
  if (task.confidence !== null && task.confidence !== undefined) {
    const parsed = Number(task.confidence);
    if (Number.isFinite(parsed)) return Math.round(parsed * 100);
  }
  return KONFIDENZ_BY_ROUTING[task.routing];
}

/** One task worklist row → the EingangMail view-model (list-level fields). */
export function mapTaskToEingangMail(task: TaskSummary): EingangMail {
  const routing = task.routing;
  return {
    id: task.id,
    absender: task.from ?? '-',
    absenderRolle: '',
    betreff: task.subject ?? task.title ?? '(kein Betreff)',
    // V10-J2: subject display name + id now joined on the list wire.
    kunde: task.kunde_name ?? '',
    kundeId: task.subject_id ?? '',
    // manual = the server routed this for human triage → «Zuordnung nötig» group
    kundeMatched: routing !== 'manual',
    kategorie: task.process,
    subjectType: task.subject_type,
    kanal: 'email',
    zeit: dateShortDe(task.received_at ?? task.created_at),
    datum: dateTimeShortDe(task.received_at ?? task.created_at),
    triage: TRIAGE_BY_STATUS[task.status],
    konfidenz: konfidenzOf(task),
    text: '',
    kiZusammenfassung: '',
    kiAntwort: '',
    felder: [],
    anhaenge: [],
    pipeline: [],
    verlauf: [],
    tags: [],
  };
}

/**
 * Overlay the split-view detail (GET /tasks/:id) onto a list-level EingangMail.
 * Fills body/attachments/activity; list-level fields win when detail is null.
 * Pure — returns a new object.
 */
export function applySplitViewToMail(mail: EingangMail, detail: SplitViewResponse): EingangMail {
  const comm = detail.communication;
  return {
    ...mail,
    betreff: comm?.subject ?? mail.betreff,
    absender: comm?.from ?? mail.absender,
    datum: comm ? dateTimeShortDe(comm.received_at) : mail.datum,
    // Carry BOTH bodies: plain-text (comm.body) and, for HTML-only mail, the HTML
    // body (comm.body_html) so the reading pane can render the actual message (Fix C).
    text: comm?.body ?? mail.text,
    html: comm?.body_html ?? mail.html,
    anhaenge: detail.attachments.map((a) => ({
      name: a.filename,
      size: fileSize(a.size_bytes),
    })),
    verlauf: detail.activity.map((entry) => ({
      time: dateTimeShortDe(entry.created_at),
      text: entry.content ?? entry.type,
      actor: entry.actor_kind === 'human' ? ('person' as const) : ('ki' as const),
    })),
  };
}

/**
 * Overlay the /content DTO onto a list-level EingangMail - body, HTML body and (U5)
 * the attachment list. Applied LAST, over the split-view fields; only call at tier
 * 'full' (the caller checks). attachments null + attachments_unavailable = the live
 * listing failed → keep the existing list, flag anhaengeUnavailable (loud state). Pure.
 */
export function applyContentToMail(
  mail: EingangMail,
  content: CommunicationContentDto,
): EingangMail {
  return {
    ...mail,
    text: content.body ?? mail.text,
    html: content.body_html ?? mail.html,
    anhaenge:
      content.attachments !== null && content.attachments !== undefined
        ? content.attachments.map((a) => ({
            name: a.filename,
            size: fileSize(a.size_bytes),
            id: a.id,
            onDemand: a.on_demand,
          }))
        : mail.anhaenge,
    anhaengeUnavailable: content.attachments_unavailable === true,
  };
}

// ── Ausgang/Gesendet mapping (V10-J2) ────────────────────────────────────────

/**
 * One communication-overview row → the AusgangMail view-model.
 * The BUCKET is the server's decision — this maps display fields only.
 */
export function mapCommToAusgangMail(
  item: CommOverviewItem,
  bucket: CommOverviewBucket,
): AusgangMail {
  return {
    id: item.id,
    betreff: item.subject ?? '(kein Betreff)',
    empfaenger: item.to ?? '-',
    kunde: '',
    kundeId: '',
    zeit: dateShortDe(item.received_at),
    herkunft: item.task_routing === 'automated' ? 'ki' : 'manuell',
    status: bucket === 'gesendet' ? 'gesendet' : 'versandbereit',
    restrictedToTeam: item.restricted_to_team,
  };
}

// ── Eingang inbox mapping (PART A3 — communications, not tasks) ───────────────

/**
 * One GET /communications?bucket=eingang row → the EingangMail view-model.
 *
 * The Eingang is now the REAL inbox: EVERY inbound comm (incl. internal / no-task)
 * is a row. The row id is the COMMUNICATION id (not a task id). The server already
 * projects fields per the viewer's tier — `subject` is null at 'metadata' — so we
 * render state, never client-side redact.
 */
export function mapCommToEingangMail(item: CommOverviewItem): EingangMail {
  const hasTask = item.task_status !== null && item.task_status !== undefined;
  const routing = item.task_routing as TaskRouting | null;
  const status = item.task_status as TaskStatus | null;
  // A comm is attached to a client when comm_reference resolved a subject (e.g. a firm
  // auto-created from the sender's domain by genesis). subject_id is the durable link.
  const hasClientLink = Boolean(item.subject_id);

  const aufgabenstatus: Aufgabenstatus = status
    ? (AUFGABENSTATUS_BY_STATUS[status] ?? 'keine')
    : 'keine';
  // triage (3-value) is only meaningful with a task; no-task rows land in "Alle" only.
  const triage: Triage = status ? (TRIAGE_BY_TASK_STATUS[status] ?? 'bearbeitung') : 'bearbeitung';

  // «Zuordnung nötig» iff a triage task exists, is routed to manual, AND the comm is not
  // yet attached to a client. A comm auto-linked to a firm (genesis) is MATCHED even while
  // its triage task is routed manual — so it no longer shows the contradictory "Kein Match".
  // No-task rows are never "unmatched" (they belong in «Keine Aufgabe»).
  const needsAssignment = hasTask && routing === 'manual' && !hasClientLink;

  // Bereich 1: a Stufe-3 (Nur Teilnehmer & Zeit) mail was ingested WITHOUT a subject —
  // the list renders an anonymized entry composed from participants + date/time, never
  // a real subject (which was never stored). Single source of wording = @brokly/shared.
  const isAnonymized = item.sync_level_captured === 3;
  const teilnehmer = [item.from, item.to].filter(Boolean).join(' · ');
  const betreff = isAnonymized
    ? formatAnonymizedEmailEntry(teilnehmer, dateTimeShortDe(item.received_at))
    : (item.subject ?? (hasTask || routing ? '(kein Betreff)' : ''));

  return {
    id: item.id,
    absender: item.from ?? '-',
    absenderRolle: '',
    // metadata tier → subject is null server-side; render an empty betreff (the
    // reading pane + list show the tier overlay instead of a real subject).
    betreff,
    kunde: item.kunde_name ?? '',
    kundeId: item.subject_id ?? '',
    kundeMatched: !needsAssignment,
    kategorie: hasTask ? 'Aufgabe' : 'Intern',
    subjectType: item.subject_type ?? null,
    tier: item.your_effective_tier,
    isPrivate: item.is_private,
    canMarkPrivate: item.can_mark_private,
    restrictedToTeam: item.restricted_to_team,
    hasTask,
    aufgabenstatus,
    kanal: commChannelToKanal(item.channel),
    zeit: dateShortDe(item.received_at),
    datum: dateTimeShortDe(item.received_at),
    triage,
    konfidenz: routing ? KONFIDENZ_BY_ROUTING[routing] : 100,
    text: '',
    kiZusammenfassung: '',
    kiAntwort: '',
    felder: [],
    anhaenge: [],
    pipeline: [],
    verlauf: [],
    tags: [],
  };
}

// ── Fetchers ──────────────────────────────────────────────────────────────────

/**
 * Fetch + map the Eingang list (GET /tasks, newest first, up to 100 rows).
 * @deprecated Superseded by fetchEingangInbox (PART A3 — the real inbox is
 *   communications, not tasks). Retained for the V10-J mappers + tests.
 */
export async function fetchEingangMails(): Promise<EingangMail[]> {
  const response = await apiClient.tasks.list({ limit: 100 });
  return response.data.map(mapTaskToEingangMail);
}

/** Fetch the split-view detail for one selected task (GET /tasks/:id). */
export function fetchEingangDetail(id: string): Promise<SplitViewResponse> {
  return apiClient.tasks.getSplitView(id);
}

/**
 * Fetch + map the Eingang inbox (GET /communications?bucket=eingang, PART A3).
 * Every inbound comm (incl. internal / no-task), newest first, up to 100 rows.
 */
export async function fetchEingangInbox(): Promise<EingangMail[]> {
  const response = await apiClient.communications.list({ bucket: 'eingang', limit: 100 });
  return response.data.map(mapCommToEingangMail);
}

/**
 * Resolve the split-view body for one Eingang COMMUNICATION (PART A3).
 *
 * There is no comm-level detail endpoint; the only body source is GET /tasks/:id.
 * So we resolve the comm's linked task (GET /tasks?comm_id=…) and read its
 * split-view. A comm with NO task (internal mail) has no body source → null.
 */
export async function fetchCommSplitView(commId: string): Promise<SplitViewResponse | null> {
  const tasks = await apiClient.tasks.list({ comm_id: commId, limit: 1 });
  const task = tasks.data[0];
  if (!task) return null;
  return apiClient.tasks.getSplitView(task.id);
}

/**
 * Fetch + map one outbound bucket (GET /communications?bucket=…, V10-J2).
 * bucket = 'ausgang_entwuerfe' (Ausgang tab) | 'gesendet' (Gesendet tab).
 */
export async function fetchAusgangMails(
  bucket: Exclude<CommOverviewBucket, 'eingang'>,
): Promise<AusgangMail[]> {
  const response = await apiClient.communications.list({ bucket, limit: 100 });
  return response.data.map((item) => mapCommToAusgangMail(item, bucket));
}
